/* ─────────────────────────────────────────────────────────────────────────
   MLMT collection break — grid controller
   ─────────────────────────────────────────────────────────────────────────
   Lifted out of sections/mlmt-collection-break.liquid on 10 Sep 2026.

   WHY: the section is placed 9 times in templates/collection.json, and a
   section's inline <style>/<script> is re-emitted per instance. Measured on
   the rendered collection page: 10 copies of the stylesheet (148 KB) and 9 of
   the controller (124 KB) — 272 KB, 17.5% of a 1,549 KB document, for two
   blocks that are byte-identical every time and contain no Liquid at all.

   As an asset it is fetched once, cached across every collection page, and
   costs the HTML nothing.

   LOADED FROM layout/theme.liquid, gated to collection pages, NOT from the
   section — nine <link>/<script> tags would work but the head is the right
   place for the stylesheet (no mid-page FOUC). If this section is ever used
   on a non-collection template, widen that gate or the break renders unstyled.
   ───────────────────────────────────────────────────────────────────────── */

(function(){
  /* ONE controller per page, however many copies of this section are on it.
     Every section renders its own <script>, so three breaks used to mean three
     controllers racing over the same grid — and each of them only ever saw the
     FIRST break, because the lookup was document.querySelector(): a singleton.
     A second copy of the section was silently ignored. The controller now
     re-queries ALL of them on every pass, so it does not matter which section's
     copy of this script won, and a break added later in the theme editor is
     picked up by the observer without a reload. */
  if (window.__mlmtCbrkController) return;
  window.__mlmtCbrkController = true;

  var busy = false;
  var cols = 0;
  var expected = null;                          /* keys the last pass placed */
  var lastCount = -1;                           /* products it placed them among */
  var lastVW = -1;                              /* viewport width it placed at */

  function grid(){ return document.querySelector('.products-grid'); }
  function tpls(){ return [].slice.call(document.querySelectorAll('[data-cbrk-ingrid]')); }

  /* How many columns the grid is ACTUALLY showing right now. Read from the
     resolved template rather than a breakpoint guess, because the grid is
     built by the theme and its column count changes with the viewport.

     ONLY SAFE TO CALL WITH NO BREAK IN THE GRID. A placed break carries an
     explicit `grid-column: 3 / span 2`; if the viewport has since dropped the
     grid to three columns, that reference to column 4 makes the browser mint
     an IMPLICIT fourth track, and this function then reports 4. Measured after
     a 1512 -> 1000 resize: "287px 287px 287px 38.9px" — three real columns and
     a 39px ghost. The old resize guard compared that 4 against the stored 4,
     concluded nothing had changed, and left the stale placement in place with
     a hole beside it. The instrument was reading its own output, so place()
     clears every break before it calls this, and the resize path no longer
     calls it at all. */
  function colCount(g){
    var t = getComputedStyle(g).gridTemplateColumns;
    if (!t || t === 'none') return 1;
    return t.trim().split(/\s+/).length;
  }

  /* THE PLACEMENT RULE — generalised from Jordan, 5 Sep: "the image should be
     the 3rd and 4th products, so products left and image right", and 5 Sep:
     "multiple images ... sizes and positions ... to keep it interactive".

     A break now carries an EXPLICIT column start, `grid-column: <start> / span
     n`. That is what makes a LEFT break, a RIGHT break and a row-spanning
     block all expressible; `span n` on its own can only say "n wide, wherever
     the cursor happens to be".

     The cost of being explicit is that CSS grid's sparse auto-placement will
     only put the block in the CURRENT row if the placement cursor is already
     sitting exactly at that column. One column early and the columns between
     are left empty; one column late and the block drops to the next row and
     leaves the tail of this one empty. That hole is the bug Jordan saw as
     "why is 2nd row only 3 items".

     So: walk the cells in DOM order tracking the cursor, and only offer the
     indices where the cursor column ALREADY equals the break's column start.
     The configured position stays a TARGET that gets nudged to the nearest
     legal one.

     DENSE PACKING MUST STAY OFF, and the code checks. With `grid-auto-flow:
     row dense` the products after a 2-row block would back-fill the gap beside
     it, which reorders products against the merchandiser's manual sort — and
     the whole reason to compute a legal slot disappears, because dense hides
     holes by moving the merchandise.

     WORKED THROUGH — 4 columns, the USP promo card occupying cell 1, a span-2
     2-row break wanted at column 3 (flush right) after 5 products:
       cells before it = 1 promo + 5 products = 6, and 6 mod 4 = 2, which is
       column start 3 counted from zero. Legal.
         row 1   promo  p1  p2  p3                      (full)
         row 2   p4     p5  [ BREAK, cols 3-4 ...
         row 3   p6     p7    ... rows 2-3 ]            (full)
         row 4   p8     p9  p10 p11
       Row 3's columns 3-4 are already taken by the block's second row, so p6
       and p7 fill columns 1-2 and the cursor steps past. Nothing is left
       behind in any row. If instead the break were inserted after 4 products
       the cursor would be at column 1, the block would be pushed to columns
       3-4 of that row, and columns 1-2 would stay empty — the hole. */

  function sideStart(side, span, c){
    if (side === 'left' || side === 'full') return 0;
    return Math.max(0, c - span);               /* right: flush to the last column */
  }

  /* Read the breaks off the page. Sorted by target position so the simulation
     below can lay them down in the order the shopper meets them. */
  function specs(c, dense){
    return tpls().map(function(t){
      var side = t.getAttribute('data-cbrk-side') || 'right';
      var span = parseInt(t.getAttribute('data-cbrk-span'), 10) || 2;
      var rows = parseInt(t.getAttribute('data-cbrk-rows'), 10) || 1;
      if (side === 'full') span = c;
      span = Math.max(1, Math.min(span, c));
      /* A 2-row block is a desktop move. On the 2-column phone grid it would
         be most of a screen, and under dense packing the placement maths above
         does not hold, so in both cases it collapses to a single row. */
      if (c <= 2 || dense) rows = 1;
      rows = Math.max(1, Math.min(rows, 2));
      return {
        tpl: t,
        key: t.getAttribute('data-cbrk-key') || '',
        pos: parseInt(t.getAttribute('data-cbrk-pos'), 10) || 6,
        span: span, rows: rows, col: sideStart(side, span, c)
      };
    }).sort(function(a, b){ return a.pos - b.pos; });
  }

  /* Lay the grid out on paper: `plan` is the breaks already committed, and the
     answer is the cursor column sitting in front of every product index. */
  function simulate(plan, nCards, lead, c){
    var occ = [], r = 0, col = 0, cursor = [];
    function taken(rr, cc){ return (occ[rr] || 0) & (1 << cc); }
    function seat(){ while (taken(r, col)){ col++; if (col >= c){ col = 0; r++; } } }
    function one(){ seat(); occ[r] = (occ[r] || 0) | (1 << col); col++; if (col >= c){ col = 0; r++; } }

    var i;
    for (i = 0; i < lead; i++) one();           /* the promo card and friends */

    var b = 0;
    for (i = 0; i < nCards; i++){
      while (b < plan.length && plan[b].before === i){
        var p = plan[b];
        seat();
        for (var rr = r; rr < r + p.rows; rr++){
          occ[rr] = occ[rr] || 0;
          for (var cc = p.col; cc < p.col + p.span; cc++) occ[rr] |= (1 << cc);
        }
        col = p.col + p.span; if (col >= c){ col = 0; r++; }
        b++;
      }
      seat();
      cursor[i] = col;
      one();
    }
    return cursor;
  }

  function place(){
    var g = grid();
    if (!g) return;

    busy = true;                                /* flag BEFORE the DOM touch */

    /* Idempotent: clear what we put in, then lay all of them down again. The
       old code refused to re-inject so a filter change could not multiply the
       tile; with N breaks that guard would have stopped after the first one,
       so the guard moved to the observer (which only calls place() when the
       set on the page and the set in the grid disagree). */
    [].forEach.call(g.querySelectorAll('[data-cbrk-placed]'), function(n){ n.remove(); });

    var cards = g.querySelectorAll('.product-card:not(.yas-promo-card), .grid__item, .product-item');
    if (!cards.length) { busy = false; return; }

    cols = colCount(g);
    var dense = /dense/.test(getComputedStyle(g).gridAutoFlow || '');
    var breaks = specs(cols, dense);
    if (!breaks.length) { busy = false; return; }

    /* Cells that sit before the first product card — the USP promo card is one
       of them, and it is why the naive index was off by one.

       Count only children that actually OCCUPY a cell. This grid also holds a
       bare <style> element with display:none; including it shifted the count
       by one and put the break in columns 2-3 instead of hard right. Measure
       what is laid out, never what is merely in the DOM. */
    var kids = Array.prototype.slice.call(g.children).filter(function(el){
      var r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    });
    var lead = kids.indexOf(cards[0]);
    if (lead < 0) lead = 0;

    var plan = [];
    breaks.forEach(function(b){
      var cursor = simulate(plan, cards.length, lead, cols);
      var best = -1, bestd = 1e9;
      for (var i = 0; i < cards.length; i++){
        if (cursor[i] !== b.col) continue;       /* the cursor is not at its column */
        if (i < cols) continue;                  /* never before a full first row —
                                                    a break in row 1 is a banner */
        /* SHORT COLLECTION GUARD. A break has to be a BREAK — something the
           shopper passes through, not the thing the page ends on. So it needs
           enough products after it to fill the columns beside it AND at least
           one whole row below it. Three of these in a 12-product collection is
           a lookbook, not a collection page; a break with no legal slot is
           simply skipped, and comes back on its own if a filter widens the
           collection (see settled(), which watches the product count). */
        if (cards.length - i < Math.max(cols, (cols - b.span) * b.rows)) continue;
        /* Keep the breaks in the order they were configured. Nudging is per
           break, so a later one can otherwise find a nearer legal slot than an
           earlier one and overtake it — measured in a 22-product collection,
           the "after 30" break landed above the "after 18" one. The breaks are
           a sequence the shopper reads, so a slot before one already taken is
           not a candidate. */
        if (plan.length && i <= plan[plan.length - 1].before) continue;
        var d = Math.abs(i - b.pos);
        if (d < bestd){ bestd = d; best = i; }
      }
      if (best < 0) return;                      /* skipped, deliberately */
      plan.push({ before: best, col: b.col, span: b.span, rows: b.rows, tpl: b.tpl, key: b.key });
      plan.sort(function(x, y){ return x.before - y.before; });
    });

    /* `cards` is a static NodeList and insertBefore does not disturb it, and
       the plan is in ascending order, so the DOM order comes out right. */
    plan.forEach(function(p){
      var node = p.tpl.cloneNode(true);
      node.removeAttribute('hidden');
      node.removeAttribute('data-cbrk-ingrid');
      node.setAttribute('data-cbrk-placed', p.key);
      /* no product card beside it to set the row height — see .mlmt-cbrk--fullrow */
      if (p.span >= cols) node.classList.add('mlmt-cbrk--fullrow');
      node.style.gridColumn = (p.col + 1) + ' / span ' + p.span;  /* grid lines are 1-based */
      node.style.gridRow = 'span ' + p.rows;
      node.style.setProperty('--cbrk-span', p.span);
      node.style.setProperty('--cbrk-rows', p.rows);
      g.insertBefore(node, cards[p.before]);
    });

    /* What this pass DECIDED on, which is not the same as what the page
       declares — the short-collection guard drops breaks on purpose. Comparing
       the observer against the declared set instead would leave it convinced
       there was work outstanding and re-running on every mutation forever. */
    expected = plan.map(function(p){ return p.key; }).sort().join('|');
    lastCount = cards.length;
    lastVW = window.innerWidth;

    setTimeout(function(){ busy = false; }, 60);
  }

  /* Does the grid hold exactly the breaks the last pass decided on? Compares
     the SET, so a filter rebuild that wipes them and a section added in the
     theme editor both come out as "work to do". The product count is part of
     the test because a filter can widen a short collection into one that now
     has room for a break the guard previously skipped — that case leaves both
     sides empty and would otherwise look settled. */
  function settled(g){
    if (expected === null) return false;
    var cards = g.querySelectorAll('.product-card:not(.yas-promo-card), .grid__item, .product-item');
    if (cards.length !== lastCount) return false;
    var have = [].map.call(g.querySelectorAll('[data-cbrk-placed]'), function(n){
      return n.getAttribute('data-cbrk-placed') || '';
    }).sort().join('|');
    return have === expected;
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', place);
  else place();

  /* the grid rebuilds itself on filter and sort — put the tiles back once it settles */
  var t;
  new MutationObserver(function(){
    if (busy) return;
    clearTimeout(t);
    t = setTimeout(function(){
      var g = grid();
      if (!busy && g && !settled(g)) place();
    }, 260);
  }).observe(document.body, { childList:true, subtree:true });

  /* The legal slot depends on the column count, so a resize that changes the
     columns invalidates every position — and the SIDE and the row span are
     resolved against it too. Re-place only when it can actually have changed;
     a plain resize listener would thrash the grid on every pixel.

     The test is the VIEWPORT WIDTH, not the column count. The column count is
     a pure function of the viewport width (the grid's breakpoints are 1024,
     768 and 480), and unlike the column count the viewport width cannot be
     contaminated by the placement we are trying to check — see colCount().
     Width and not height, so a phone collapsing its address bar mid-scroll
     does not rebuild the grid under the shopper's thumb. */
  var r;
  window.addEventListener('resize', function(){
    clearTimeout(r);
    r = setTimeout(function(){
      if (!grid()) return;
      if (window.innerWidth === lastVW) return;
      place();
    }, 200);
  });
})();
